import { unwrapEnvelope } from '@/model/client';
import {
  useGetApiWorkspacesMeProjectCountsQuery,
  useGetApiWorkspacesMeQuery,
} from '@/model/workspace/api.generated';
import type { ListWorkspacesResponse, WorkspaceResponse } from '@/model/workspace/dto.generated';
import { useAppSelector } from '@/shared/hooks/useStore';
import { selectUserData } from '@/viewmodel/features/user';

export type WorkspaceProjectCountMap = Record<string, number>;

export const useWorkspaceQuery = (): {
  workspaces: WorkspaceResponse[];
  projectCountMap: WorkspaceProjectCountMap;
  isLoading: boolean;
  isLoaded: boolean;
  isSettled: boolean;
} => {
  const userData = useAppSelector(selectUserData);
  const userEmail = userData.data?.email;
  const queryArgs = userEmail ? { userEmail } : {};
  const skip = !userEmail;

  const {
    data,
    isLoading: workspacesLoading,
    isSuccess,
    isError,
  } = useGetApiWorkspacesMeQuery(queryArgs, {
    skip,
    refetchOnMountOrArgChange: 30,
  });

  const { data: countsData, isLoading: countsLoading } = useGetApiWorkspacesMeProjectCountsQuery(
    queryArgs,
    { skip, refetchOnMountOrArgChange: 30 },
  );

  const workspaces: WorkspaceResponse[] = data
    ? (unwrapEnvelope<ListWorkspacesResponse>(data).items ?? [])
    : [];

  const projectCountMap: WorkspaceProjectCountMap = {};
  for (const item of countsData?.['data']?.items ?? []) {
    if (item.workspace_id !== undefined) {
      projectCountMap[item.workspace_id] = item.project_count ?? 0;
    }
  }

  return {
    workspaces,
    projectCountMap,
    isLoading: workspacesLoading || countsLoading,
    isLoaded: data !== undefined,
    isSettled: isSuccess || isError,
  };
};
